import { getWhiteNoiseStream } from 'CHECKS/connectivity/noise';
import { createCall } from 'CHECKS/connectivity/call';
import { createStatsWatcher } from 'CHECKS/connectivity/stats';
import { DUMMY_LOGGER } from 'UTILS/constants';
import { navigatorDetails } from 'UTILS/navigator';

const RAMP_UP_DELAY = 5000;
const CONNECTION_QUALITY_CHECK_DURATION = 15000;
const START_BITRATE = 180;
// iOS needs bigger frame to reach high bitrate
const FRAME_WIDTH = navigatorDetails.os === 'iOS' ? 400 : 200;
const FRAME_HEIGHT = navigatorDetails.os === 'iOS' ? 400 : 100;
export const TOTAL_CONNECTION_QUALITY_DURATION = RAMP_UP_DELAY + CONNECTION_QUALITY_CHECK_DURATION;

/**
 * Create a WebRTC call with oneself and compute various indicators about the connection quality.
 * @param {Array<RTCIceServer>} relayServers - ICE Servers that are required for the statistics to be relevant
 * (self-to-self without a relay is not an actual use case).
 * @param {{warn: function, debug: function, log: function, error: function, info: function}} [logger] - The logger to be used.
 * @returns {Promise<{packetLossPercent: number, latency: number, bitrate: number, quality: number, }>} Resolves after gathering enough statistics.
 */
export const computeConnectionQuality = async({
  relayServers,
  logger = DUMMY_LOGGER,
}) => new Promise(async(resolve, reject) => {
  const results = {};
  try {
    let call;
    const stream = getWhiteNoiseStream(FRAME_WIDTH, FRAME_HEIGHT, 30);
    call = await createCall({
      stream,
      iceConfig: {
        iceServers: relayServers,
        iceTransportPolicy: 'relay',
      },
    });
    const startTime = window.performance.now();

    let statsWatcher;
    setTimeout(() => {
      const handleCallStats = ({ videoStats }) => {
        let target = (FRAME_WIDTH * FRAME_HEIGHT) / 64; // Arbitrary value to approximate the targeted used bandwidth
        results.packetLossPercent = videoStats.packetLossPercent;
        results.latency = videoStats.latencyMsAvg;
        results.bitrate = videoStats.kiloBytesSentRateAvg * 8;

        const millisSinceStart = window.performance.now() - startTime;

        const anticipatedTime = Math.max(0, millisSinceStart - 1000);
        target = Math.min(target, START_BITRATE * Math.pow(1.08, (anticipatedTime) / 1000)); // Expect 8% growth every second (see https://tools.ietf.org/html/draft-ietf-rmcat-gcc-02#section-5.5)

        results.quality = Math.min(100, 100 * ((videoStats.kiloBytesSentRateAvg * 8) / (target * 0.9)));
        logger.log('Quality - ', { ...results, target });
      };

      statsWatcher = createStatsWatcher({
        peerConnection: call.pcA,
        stream,
        logger,
        onStats: handleCallStats,
      });
    }, RAMP_UP_DELAY);

    setTimeout(() => {
      statsWatcher.stop();
      call.stop();
      resolve(results);
    }, TOTAL_CONNECTION_QUALITY_DURATION);
  } catch (err) {
    reject(err);
  }
});

